import React, { useRef, useState, useMemo, useEffect } from 'react';
import { VStack, HStack, Box, Input, Flex, Icon, Spinner, Text, Image, Button, Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { FaWhatsapp } from "react-icons/fa";
import { IoSend } from 'react-icons/io5';
import { PiImageFill } from 'react-icons/pi';
import { HiPlusCircle } from 'react-icons/hi2';

import { useChat } from '@/components/providers/ChatProvider';
import ChatBubble from '@/components/ui/chat/ChatBubble';
import InfoBubble from '@/components/ui/chat/InfoBubble';
import ApiBubble from '@/components/ui/chat/ApiBubble';
import WhatsUpBubble from '@/components/ui/chat/WhatsUpBubble';
import TypingIndicator from '@/components/ui/chat/TypingIndicator';
import ChatInputArea from "@/components/ui/chat/ChatInputArea";
import CyberJobLogo from '@/assets/images/cyberjob_dialogflow_logo.svg';
import ScenarioSelect from "@/pages/chat/ScenarioSelect";

const FullScreenChatUI = ({ chat, onSendMessage, streamingState, isSending, onScenarioSelected, scenarios, selectedScenario, scenarioCollection, onWhatsUpYes, onWhatsUpNo }) => {
    const chatEndRef = useRef(null); // Ref to scroll to the bottom
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLinked, setIsLinked] = useState(false);
    const { linkWhatsApp } = useChat();

    const linkToWhatsApp = async (phone, chat, onSuccess) => {
        setIsLinking(true);

        linkWhatsApp(phone, chat)
            .then(() => {
                setIsLinked(true);
                setIsDialogOpen(false);
                onSuccess && onSuccess();
            })
            .finally(() => setIsLinking(false))
            .catch(err => console.error('Failed to link WhatsApp', err))
    }

    const linkToWhatsAppButton = (phone, chat) => {
        linkWhatsApp(phone, chat)
            .then(() => {
                setIsLinked(true);
                setIsDialogOpen(false);
            })
            .finally(() => setIsLinking(false))
            .catch(err => console.error('Failed to link WhatsApp', err))
    }

    const handleOnYes = (phone, chat, onSuccess) => {
        linkToWhatsApp(phone, chat, onSuccess);
    }

    const chatContent = useMemo(() => {
        if (chat?.messages && chat?.messages?.length > 0) {
            console.log('[FullScreenChatUI] 🚀 ', JSON.stringify(chat?.messages, null, 2));
            return chat?.messages?.map((message, index) => {
                if (message.type === 'info') return <InfoBubble message={message} key={index} />;
                if (message.type === 'api') return <ApiBubble message={message} key={index} />;
                if (message.type !== 'image' && message.type !== 'file' && message.type !== 'document' && message.text.includes('{%WhatsApp_Start}')) return <WhatsUpBubble message={message} key={index} onYes={(phone, onSuccess) => handleOnYes(phone, chat, onSuccess)} onNo={onWhatsUpNo} />;
                return <ChatBubble message={message} key={index} />;
            });
        }
    }, [chat?.messages]);

    // Scroll to bottom whenever chat content or streaming message changes
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chat?.messages, streamingState]);


    return (
        <VStack h="100vh" w="full" bg="bg.primary" spacing={0} justifyContent="space-between" alignItems="center" pt={4}>
            <HStack w="full" px={4} py={2} pb={4} borderBottomWidth="1px" borderBottomColor="border.primary" justifyContent="center">
                <Box flex={1}>
                    <ScenarioSelect
                        collection={scenarioCollection}
                        selectedScenario={selectedScenario}
                        scenarios={scenarios}
                        onScenarioChange={onScenarioSelected}
                        size="sm"
                        maxW="280px"
                    />
                </Box>
                <Image src={CyberJobLogo} alt="CyberJob Logo" w={'120px'} />
                <HStack flex={1} justifyContent="flex-end">
                    <Button
                        size="sm"
                        colorScheme="green"
                        variant={isLinked ? 'solid' : 'outline'}
                        onClick={() => !isLinked && setIsDialogOpen(true)}
                        disabled={isLinked}
                    >
                        <FaWhatsapp />
                        {isLinked ? 'WhatsApp connected' : 'Connect WhatsApp'}
                    </Button>
                </HStack>

                <Dialog.Root open={isDialogOpen} onOpenChange={(e) => setIsDialogOpen(e.open)}>
                    <Portal>
                        <Dialog.Backdrop />
                        <Dialog.Positioner>
                            <Dialog.Content>
                                <Dialog.Body pt={6} pb={4} px={6}>
                                    <Text fontWeight="semibold" fontSize="lg" mb={4}>Connect WhatsApp</Text>
                                    <Text mb={2} fontSize="sm">Enter phone number in format +XXXXXXXXXXX:</Text>
                                    <Input
                                        placeholder="+48601234567"
                                        value={phoneNumber}
                                        onChange={e => setPhoneNumber(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && linkToWhatsAppButton(phoneNumber, chat)}
                                    />
                                    <HStack mt={4} justifyContent="flex-end">
                                        <Button
                                            colorScheme="green"
                                            loading={isLinking}
                                            onClick={() => linkToWhatsAppButton(phoneNumber, chat)}
                                        >
                                            Connect
                                        </Button>
                                    </HStack>
                                </Dialog.Body>
                                <Dialog.CloseTrigger asChild>
                                    <CloseButton size="sm" position="absolute" top="2" right="2" />
                                </Dialog.CloseTrigger>
                            </Dialog.Content>
                        </Dialog.Positioner>
                    </Portal>
                </Dialog.Root>
            </HStack>

            {/* Main Message Display Area */}
            <VStack w="45%" h={'100%'} overflowY="auto" p={4} spacing={4} justifyContent={'flex-start'} alignItems={'flex-start'}>
                {chatContent}
                {streamingState?.chatId === chat?.id && (
                    <ChatBubble message={{ text: streamingState?.displayText, user: 'bot', timestamp: new Date().toISOString() }} />
                )}
                {chat?.isBotTyping && !streamingState && <TypingIndicator />}
                <div ref={chatEndRef} />
            </VStack>

            {/* Input Field at the Bottom */}
            <Box w="full" bg="bg.chatFrame" borderTopWidth="1px" borderTopColor="border.primary" py={2}>
                <ChatInputArea onSendMessage={onSendMessage} isSending={isSending} isFullscreen />
            </Box>
        </VStack>
    );
};

export default FullScreenChatUI;
